import { BlogPost, SuccessStory } from '../types/content';
import { mockBlogPosts, mockSuccessStories } from './mockData';
import { invokeLambda } from './lambdaClient';
import { fetchLinkMetadata } from './metadataService';

// Simulate API delay
const simulateDelay = (ms: number = 500): Promise<void> => 
  new Promise(resolve => setTimeout(resolve, ms));

export class ContentService {
  // Get all blog posts

  static async getAllBlogPosts(): Promise<BlogPost[]> {
    try {
      const response = await invokeLambda<any[]>({
        action: 'getAllBlogs',
        payload: {}
      });

      if (response.success && response.data) {
        // Fetch metadata for all blogs in parallel
        const enrichedBlogs = await Promise.all(response.data.map(async (blog: any) => {
          let metadata = {};
          if (blog.link) {
            metadata = await fetchLinkMetadata(blog.link);
          }
          
          // Merge API data with metadata, preferring metadata for some fields if available
          // or using API data as fallback
          const meta = metadata as any;
          
          return {
            id: blog.blogID.toString(),
            title: meta.title || blog.title,
            excerpt: meta.description || blog.description,
            content: meta.description || blog.description,
            featuredImage: {
              url: meta.image || 'https://placehold.co/600x400?text=Blog',
              altText: meta.title || blog.title,
              type: 'image' as const
            },
            author: {
              id: 'external',
              name: meta.author || meta.publisher || 'HCLTech Team',
              title: 'Contributor'
            },
            publishDate: meta.date ? new Date(meta.date) : new Date(),
            tags: [],
            relatedSolutions: [],
            status: blog.active ? 'published' : 'draft',
            link: blog.link
          };
        }));

        return enrichedBlogs as BlogPost[];
      }
      return [];
    } catch (error) {
      console.error('Error fetching blogs:', error);
      return [];
    }
  }

  // Get blog post by ID
  static async getBlogPostById(id: string): Promise<BlogPost | null> {
    await simulateDelay();
    return mockBlogPosts.find(post => post.id === id) || null;
  }

  // Get all success stories
  static async getAllSuccessStories(): Promise<SuccessStory[]> {
    await simulateDelay();
    return mockSuccessStories.filter(story => story.status === 'published');
  }

  // Get success story by ID
  static async getSuccessStoryById(id: string): Promise<SuccessStory | null> {
    await simulateDelay();
    return mockSuccessStories.find(story => story.id === id) || null;
  }

  // Get all content (blogs only)
  static async getAllContent(): Promise<BlogPost[]> {
    await simulateDelay();
    const blogs = await this.getAllBlogPosts();
    
    // Sort by publish date (newest first)
    return blogs.sort((a, b) => 
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
  }

  // Get featured content for home page
  static async getFeaturedContent(limit: number = 4): Promise<(BlogPost | SuccessStory)[]> {
    await simulateDelay();
    const allContent = await this.getAllContent();
    return allContent.slice(0, limit);
  }

  // Get content by tags
  static async getContentByTags(tags: string[]): Promise<(BlogPost | SuccessStory)[]> {
    await simulateDelay();
    const allContent = await this.getAllContent();
    
    return allContent.filter(content =>
      content.tags.some(tag => tags.includes(tag))
    );
  }

  // Get content related to solution
  static async getContentBySolutionId(solutionId: string): Promise<(BlogPost | SuccessStory)[]> {
    await simulateDelay();
    const blogs = mockBlogPosts.filter(post => 
      post.status === 'published' && post.relatedSolutions.includes(solutionId)
    );
    const stories = mockSuccessStories.filter(story => 
      story.status === 'published' && story.relatedSolutions.includes(solutionId)
    );
    
    return [...blogs, ...stories].sort((a, b) => 
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
  }

  // Search content
  static async searchContent(query: string): Promise<(BlogPost | SuccessStory)[]> {
    await simulateDelay();
    const searchTerm = query.toLowerCase();
    const allContent = await this.getAllContent();
    
    return allContent.filter(content =>
      content.title.toLowerCase().includes(searchTerm) ||
      content.excerpt.toLowerCase().includes(searchTerm) ||
      content.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  }
}